import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSystem } from '@ohif/core';
import { usePolling } from './usePolling';
import { OrganEnum, OrganQualityEnum } from '../clients/viewer';
import { isSubmittable, OrganRow, OrganReviewService } from '../services/OrganReviewService';

const NO_ROWS: readonly OrganRow[] = Object.freeze([]);

export function useOrganReview(
  studyInstanceUid?: string,
  seriesInstanceUid?: string
): {
  rows: readonly OrganRow[];
  hasPending: boolean;
  isFullyReviewed: boolean;
  isSubmitDisabled: boolean;
  hasError: boolean;
  setQuality: (organ: OrganEnum, quality?: OrganQualityEnum) => void;
  submit: () => Promise<void>;
  refresh: () => Promise<void>;
} {
  const { servicesManager } = useSystem();
  const { organReviewService, uiNotificationService } = servicesManager.services;

  const [rows, setRows] = useState<readonly OrganRow[]>(() =>
    studyInstanceUid && seriesInstanceUid
      ? organReviewService.getRows(studyInstanceUid, seriesInstanceUid)
      : NO_ROWS
  );

  const [hasError, setHasError] = useState(false);

  const refresh = useCallback(
    () =>
      studyInstanceUid && seriesInstanceUid
        ? organReviewService.refresh(studyInstanceUid, seriesInstanceUid)
        : Promise.resolve(),
    [organReviewService, studyInstanceUid, seriesInstanceUid]
  );

  const { startPolling, stopPolling } = usePolling(refresh);

  useEffect(() => {
    if (!studyInstanceUid || !seriesInstanceUid) {
      setRows(NO_ROWS);
      setHasError(false);
      return undefined;
    }

    const apply = () => {
      const next = organReviewService.getRows(studyInstanceUid, seriesInstanceUid);
      setRows(next);

      if (!organReviewService.hasError(seriesInstanceUid) &&
          next.some(row => row.state === 'inProgress')) {
        startPolling(refresh);
      } else {
        stopPolling();
      }

      setHasError(organReviewService.hasError(seriesInstanceUid));
    };

    const matches = (event: { studyInstanceUid: string; seriesInstanceUid: string }) =>
      event.studyInstanceUid === studyInstanceUid &&
      event.seriesInstanceUid === seriesInstanceUid;

    const subscriptions = [
      organReviewService.subscribe(
        OrganReviewService.EVENTS.ORGAN_REVIEWS_CHANGED,
        event => matches(event) && apply()
      ),

      organReviewService.subscribe(
        OrganReviewService.EVENTS.SESSION_LOST,
        event => matches(event) && stopPolling()
      ),
    ];

    apply();
    void refresh();

    return () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      stopPolling();
    };
  }, [organReviewService, studyInstanceUid, seriesInstanceUid, refresh, startPolling, stopPolling]);

  const setQuality = useCallback(
    (organ: OrganEnum, quality?: OrganQualityEnum) => {
      if (studyInstanceUid && seriesInstanceUid) {
        organReviewService.setQuality(studyInstanceUid, seriesInstanceUid, organ, quality);
      }
    },
    [organReviewService, studyInstanceUid, seriesInstanceUid]
  );

  const submit = useCallback(async () => {
    if (!studyInstanceUid || !seriesInstanceUid) {
      return;
    }

    if (organReviewService.getSubmittable(studyInstanceUid, seriesInstanceUid).length === 0) {
      return;
    }

    try {
      await organReviewService.submit(studyInstanceUid, seriesInstanceUid);

      uiNotificationService?.show({
        title: 'Organ Review',
        message: 'Reviews submitted successfully',
        type: 'success',
        duration: 5000,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      uiNotificationService?.show({
        title: 'Organ Review',
        message: 'Failed to submit reviews',
        type: 'error',
        duration: 5000,
      });
    }
  }, [organReviewService, uiNotificationService, studyInstanceUid, seriesInstanceUid]);

  return useMemo(() => {
    const hasPending = rows.some(row => row.state === 'inProgress');

    return {
      rows,
      hasPending,
      hasError,
      isFullyReviewed: rows.length > 0 && rows.every(row => row.state === 'complete'),

      isSubmitDisabled: hasPending || !rows.some(isSubmittable),

      setQuality,
      submit,
      refresh,
    };
  }, [rows, hasError, setQuality, submit, refresh]);
}
